import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    ChevronRightChunky as ChevronRightChunkySvg,
    CrossChunky as CrossChunkySvg,
} from '@eventbrite/eds-iconography';
import queryString, { ParsedQuery } from 'query-string';
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    FILTER_DRAWER_DISPLAY_ORDER,
} from '../../../../constants/filters';
import { SelectedFilters } from '../../../../types/index';
import { SelectedDate } from './DateFilterSection';
import DuplicateEventsFilterButton from './DuplicateEventsFilterButton';
import FilterButton from './FilterButton';
import './FilterPanelDrawers.scss';
import FollowedOrganizersFilterButton from './FollowedOrganizersFilterButton';
import OnlineEventsFilterButton from './OnlineEventsFilterButton';
import OrganizationFilterButton from './OrganizationFilterButton';

interface FilterPanelDrawerProps {
    selectedFilters?: SelectedFilters;
    handleDrawerSelect?: Function;
    handleRemoveFilter?: Function;
    activeFilter?: boolean;
    children: React.ReactElement<any, any>;
    filterType: string;
    isMobile?: boolean;
    onClick?: Function;
}

const getFilterTypeLabel = (filterType: string) =>
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filterType].label;

const getFilterValueLabel = (
    filterType: string,
    activeFilter: string,
    urlParams: ParsedQuery<string>,
) => {
    const currentFilter = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filterType];
    const activeValues =
        typeof activeFilter === 'string' ? [activeFilter] : [...activeFilter];

    let activeFilterValueDisplay = currentFilter.values
        .filter(({ value }: { value: string }) => activeValues.includes(value))
        .map(({ display }: { display: string }) => display)
        .join(', ');

    //Wasn't able to find filter value and
    //is dates, then custom date range most likely
    if (!activeFilterValueDisplay && filterType === 'dates') {
        activeFilterValueDisplay = <SelectedDate urlParams={urlParams} />;
    }

    return activeFilterValueDisplay;
};

const FilterPanelDrawerContent = ({
    activeFilter,
    filterType,
    location,
}: {
    activeFilter: string;
    filterType: string;
    location: { search: string };
}) => {
    const filterDisplay = getFilterTypeLabel(filterType);
    const urlParams = queryString.parse(location.search.replace('?', ''));

    return (
        <>
            {activeFilter ? (
                <div>
                    <div className="filter-panel__drawers__item__sub-header">
                        {filterDisplay}
                    </div>
                    <div
                        className="filter-panel__drawers__item__header--active"
                        data-testid="filter-panel__drawers__item__header--active"
                    >
                        {getFilterValueLabel(
                            filterType,
                            activeFilter,
                            urlParams,
                        )}
                    </div>
                </div>
            ) : (
                <div className="filter-panel__drawers__item__header">
                    {filterDisplay}
                </div>
            )}
            {!activeFilter && <Icon type={<ChevronRightChunkySvg />} />}
        </>
    );
};

const EnhancedDrawerContent = withRouter(
    FilterPanelDrawerContent as any,
) as any & Pick<FilterPanelDrawerProps, 'selectedFilters'>;

export const FilterPanelDrawer = ({
    activeFilter,
    children,
    filterType,
    handleRemoveFilter,
    isMobile,
    onClick,
}: FilterPanelDrawerProps) => {
    const filterDisplay = getFilterTypeLabel(filterType || '');

    return (
        <>
            <FilterButton
                isActive={!!activeFilter}
                onClick={() => onClick?.(filterType)}
                value={filterType}
                isMobile={isMobile}
            >
                {children}
            </FilterButton>
            {activeFilter && (
                <div
                    className="filter-panel__drawers__item--close-btn"
                    data-testid="filter-panel__drawers__item--close-btn"
                >
                    <IconButton
                        title={`Remove ${filterDisplay} filter`}
                        iconType={<CrossChunkySvg />}
                        iconColor="ui-blue"
                        onClick={() =>
                            handleRemoveFilter?.({ [filterType]: true })
                        }
                    />
                </div>
            )}
        </>
    );
};

interface FilterPanelDrawersProps
    extends Pick<
        FilterPanelDrawerProps,
        | 'handleDrawerSelect'
        | 'handleRemoveFilter'
        | 'selectedFilters'
        | 'isMobile'
    > {
    isLoading?: boolean;
}

export const FilterPanelDrawers = (props: FilterPanelDrawersProps) => {
    return (
        <ul
            className="filter-panel__drawers"
            data-testid="filter-panel__drawers"
        >
            <>
                <li className="eds-l-pad-top-6 eds-l-pad-bot-5 eds-l-pad-left-6 eds-l-pad-right-6">
                    <FollowedOrganizersFilterButton {...props} />
                </li>
                <li className="eds-l-pad-top-6 eds-l-pad-bot-5 eds-l-pad-left-6 eds-l-pad-right-6">
                    <OnlineEventsFilterButton {...props} />
                </li>
            </>
            {!props.isLoading &&
                (props.selectedFilters?.organizations.length || 0) > 0 && (
                    <OrganizationFilterButton />
                )}
            {!props.isLoading && props.selectedFilters?.hash && (
                <DuplicateEventsFilterButton />
            )}
            <div className="eds-l-pad-vert-3 eds-l-pad-left-6 eds-l-pad-right-6">
                <div className="filter-panel__drawers__divider" />
            </div>
            {FILTER_DRAWER_DISPLAY_ORDER.map((filterType, index) => {
                if (filterType === 'divider') {
                    return (
                        <div
                            key={`${
                                FILTER_DRAWER_DISPLAY_ORDER[index - 1]
                            }-divider`}
                            className="eds-l-pad-vert-3 eds-l-pad-left-6 eds-l-pad-right-6"
                        >
                            <div className="filter-panel__drawers__divider" />
                        </div>
                    );
                }

                return (
                    <li
                        key={filterType}
                        className="filter-panel__drawers__item"
                    >
                        <FilterPanelDrawer
                            filterType={filterType}
                            onClick={props.handleDrawerSelect}
                            activeFilter={!!props.selectedFilters?.[filterType]}
                            handleRemoveFilter={props.handleRemoveFilter}
                            isMobile={props.isMobile}
                        >
                            <EnhancedDrawerContent
                                activeFilter={
                                    props.selectedFilters?.[filterType]
                                }
                                filterType={filterType}
                            />
                        </FilterPanelDrawer>
                    </li>
                );
            })}
        </ul>
    );
};

export default FilterPanelDrawers;
